import { useEffect } from 'react';

// Per-route SEO for public pages (PRD Module 7.9). CRA renders client-side, so
// we set the document title and meta/OG/Twitter tags imperatively — no extra
// dependency. Crawlers that execute JS (Google) read these; the static tags in
// index.html cover the rest.

function upsertMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

export interface SeoOptions {
  title: string;
  description?: string;
  image?: string;
  noIndex?: boolean;
}

export function useSeo({ title, description, image, noIndex }: SeoOptions) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', 'name', 'description', description);
      upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
      upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', window.location.href);
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    const ogImg = image
      ? (image.startsWith('http') ? image : `${window.location.origin}${image.startsWith('/') ? '' : '/'}${image}`)
      : 'https://cuet.webfoxx.com/og-image.jpg';

    upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImg);
    upsertMeta('meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', ogImg);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImg);
    // Private/app pages should never be indexed.
    upsertMeta('meta[name="robots"]', 'name', 'robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    upsertCanonical(window.location.origin + window.location.pathname);

    return () => { document.title = previousTitle; };
  }, [title, description, image, noIndex]);
}
